import { LogInPaths } from '@bbkAdminComponents/log-in-flow/log-in-paths';

/**
 * Auth-landing pages are never valid deep-link targets. Accepting one lets a
 * self-referential `redirectUri` (e.g. `/company/sign-in`) bounce the user
 * through an extra sign-in reload before the selector, and semantically a deep
 * link should never resolve to the sign-in/selector/exchange/logout entry
 * points. Compared on pathname only (trailing slash normalized) so real deep
 * links like `/company/acme/app/123/...` are unaffected.
 */
const AUTH_LANDING_PATHS = new Set<string>([
  LogInPaths.company, // /company (and /company/)
  LogInPaths.selector, // /company/selector
  LogInPaths.signIn, // /company/sign-in
  LogInPaths.cbidpExchange, // /cbidp-exchange
  LogInPaths.logout, // /logout
]);

/**
 * Query parameters that give `/company/sign-in` something to do after the user
 * authenticates, which is what separates a pass-through wrapper from a
 * self-referential redirect. `redirectUri` is the deep link the landing
 * forwards to; `companyName` is the pending company from trial signup that the
 * landing creates before continuing to the setup step.
 */
const SIGN_IN_PASS_THROUGH_PARAMS = ['redirectUri', 'companyName'];

const normalizePath = (pathname: string): string =>
  pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;

/**
 * True when a redirect target would leave the current origin — either an
 * absolute URL to a different origin, or a `//host` / `/\host` path that
 * `window.location.assign` treats as protocol-relative (and unparseable input,
 * which we treat as unsafe). This is the subset of `toSafeInternalRedirect`'s
 * rejections that represents a genuine host mismatch, as opposed to merely
 * declining a same-origin auth-landing path. `cbidp-exchange` uses it so its
 * "Redirect Blocked for Security" screen fires only for real cross-origin
 * attempts, not for e.g. a bare `/company/sign-in` from a plain sign-in.
 */
export const isOffOriginRedirect = (
  target: string | undefined | null
): boolean => {
  if (!target) {
    return false;
  }
  try {
    const url = new URL(target, window.location.origin);
    if (url.origin !== window.location.origin) {
      return true;
    }
    return /^\/[/\\]/.test(url.pathname + url.search + url.hash);
  } catch {
    return true;
  }
};

/**
 * Resolves a redirect target to a same-origin relative path. Anything that
 * points off-origin (or cannot be parsed) collapses to the fallback so a
 * query-param-supplied `redirectUri` cannot be used for an open redirect.
 * Mirrors the same-origin validation used in `cbidp-exchange`.
 */
export const toSafeInternalRedirect = (
  target: string | undefined | null,
  fallback: string = LogInPaths.company
): string => {
  if (!target) {
    return fallback;
  }
  // A "//host" / "/\\host" path passes the origin check but assign would treat
  // it as off-origin; an absolute off-origin URL is rejected too.
  if (isOffOriginRedirect(target)) {
    return fallback;
  }
  try {
    const url = new URL(target, window.location.origin);
    const relativePath = url.pathname + url.search + url.hash;
    if (AUTH_LANDING_PATHS.has(normalizePath(url.pathname))) {
      // Exception: a `/company/sign-in` that carries work for the sign-in
      // landing to do is a pass-through wrapper, not a self-referential loop,
      // and the CBIDP flow round-trips it through the IdP before handing it to
      // `cbidp-exchange`. It must survive so the landing still receives its
      // parameters (a deep link, which it re-validates via this same helper; or
      // the pending company from trial signup, which it creates). A bare
      // `/company/sign-in`, or any other auth-landing target, still collapses
      // to the fallback, so a self-referential redirect cannot loop.
      const isSignInPassThrough =
        normalizePath(url.pathname) === LogInPaths.signIn &&
        SIGN_IN_PASS_THROUGH_PARAMS.some((param) =>
          url.searchParams.get(param)
        );
      if (!isSignInPassThrough) {
        return fallback;
      }
    }
    return relativePath;
  } catch {
    return fallback;
  }
};
